import { useEffect, useState } from 'react';

import {
  typographyPTSansNarrow10,
  typographyParagraphSmallPrimaryRegular,
} from 'common-ui/typography';
import { styled } from 'style/ORSNNTheme';
import { Button, Dialog, DialogProps } from 'ui-kit';

import { CloseButton, CompactDialogContainer } from '../deals/dealCards/Diligence/commonStyles';
import { Note } from '../deals/dealCards/Diligence/DealDocumentTypes';

interface NotesDialogProps {
  documentName: string;
  notes: Note[];
  handleAddNote: (text: string) => void;
  dialog: DialogProps;
}

const NotesDialog: React.FC<NotesDialogProps> = ({
  documentName,
  notes,
  handleAddNote,
  dialog,
}) => {
  const [noteText, setNoteText] = useState<string>('');

  useEffect(() => {
    if (dialog.isOpen) {
      setNoteText('');
    }
  }, [dialog.isOpen]);

  return (
    <Dialog dialog={dialog} contentComponent={CompactDialogContainer}>
      <h1 className="relative mx-0 mb-[10px] mt-[3px] p-0 text-[12px] font-medium leading-[16px] tracking-[0] text-[#afafaf] [font-family:Heebo,sans-serif]">
        Add a Note for "{documentName}"
        <CloseButton onClick={() => dialog.setIsOpen(false)} />
      </h1>
      <NoteHistory>
        {notes.map((note) => (
          <div key={note.noteID}>
            <NoteHeader>
              <span>({note.authorRole})</span>
            </NoteHeader>
            <NoteDate>{new Date(note.createdAt).toDateString()}</NoteDate>
            <NoteText>{note.note}</NoteText>
          </div>
        ))}
      </NoteHistory>
      <NoteEditor
        aria-label="Add Note"
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
      />
      <ButtonsContainer>
        <Button
          type="secondary"
          label="cancel"
          size="medium"
          onClick={() => dialog.setIsOpen(false)}
        >
          Cancel
        </Button>
        <Button
          label="add-note"
          size="medium"
          disabled={noteText.length === 0}
          onClick={() => {
            handleAddNote(noteText);
            setNoteText('');
          }}
        >
          Add Note
        </Button>
      </ButtonsContainer>
    </Dialog>
  );
};

export default NotesDialog;

const NoteHistory = styled.div`
  max-height: 444px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: 20px;
`;

const NoteHeader = styled.div`
  color: ${(props) => props.theme.color.gray500};
`;

const NoteDate = styled.div`
  ${typographyPTSansNarrow10}
  color: ${(props) => props.theme.color.slate200};
  margin-bottom: 5px;
`;

const NoteText = styled.div`
  ${typographyParagraphSmallPrimaryRegular}
`;

const NoteEditor = styled.textarea`
  ${typographyParagraphSmallPrimaryRegular}
  border: 1px solid #e85ee5;
  border-radius: 4px;
  margin: 15px 0;
  padding: 5px 10px;
  width: 100%;
  height: 118px;
  color: ${(props) => props.theme.color.fgDefault};
  background-color: ${(props) => props.theme.color.bgSurface};

  &:focus {
    outline: none;
    border: 1px solid #e85ee5;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;
